import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import {
    Loader2, Copy, CheckCircle2, ChevronRight,
    ArrowDownLeft, ArrowDownRight, User, Package, ClipboardList,
} from "lucide-react";

const FIELD = "mt-1.5 w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-[#2172E5] focus:ring-2 focus:ring-blue-100 text-sm bg-white";
const TEXTAREA = "mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#2172E5] focus:ring-2 focus:ring-blue-100 text-sm resize-none bg-white";
const LABEL = "text-sm font-semibold text-slate-700";

const STEPS = [
    { label: "Sponsor Details", icon: User },
    { label: "Choose Plan", icon: Package },
    { label: "Personal Info", icon: ClipboardList },
];

function StepIndicator({ current }) {
    return (
        <div className="flex items-center gap-0 mb-8">
            {STEPS.map((s, i) => {
                const done = i < current;
                const active = i === current;
                const Icon = s.icon;
                return (
                    <div key={i} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors
                                ${done ? "bg-[#2172E5] border-[#2172E5]" : active ? "border-[#2172E5] bg-white" : "border-slate-200 bg-white"}`}>
                                {done
                                    ? <CheckCircle2 className="w-5 h-5 text-white" />
                                    : <Icon className={`w-4 h-4 ${active ? "text-[#2172E5]" : "text-slate-400"}`} />}
                            </div>
                            <span className={`text-[10px] font-semibold whitespace-nowrap ${active ? "text-[#2172E5]" : done ? "text-slate-500" : "text-slate-400"}`}>
                                {s.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-1 mb-4 ${done ? "bg-[#2172E5]" : "bg-slate-200"}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Step 1: Sponsor lookup ────────────────────────────────────────────────────
function Step1({ defaultCode, onConfirm }) {
    const [code, setCode] = useState(defaultCode || "");
    const [side, setSide] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const verify = async () => {
        if (!code.trim()) { setError("Please enter a Sponsor ID"); return; }
        if (!side) { setError("Please choose a side"); return; }
        setError("");
        setLoading(true);
        try {
            const { data } = await api.get("/auth/sponsor-lookup", {
                params: { code: code.trim().toUpperCase(), side },
            });
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Sponsor ID not found");
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSide = (s) => {
        setSide(s);
        setResult(null);
        setError("");
    };

    return (
        <div className="space-y-5">
            <div>
                <label className={LABEL}>Sponsor&apos;s ID <span className="text-red-500">*</span></label>
                <input
                    className={FIELD}
                    placeholder="Enter referral / Member ID"
                    value={code}
                    onChange={e => { setCode(e.target.value); setResult(null); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && verify()}
                />
            </div>

            <div>
                <div className={LABEL + " mb-1.5"}>Place On Side <span className="text-red-500">*</span></div>
                <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => handleSide("L")}
                        className={`h-11 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors
                            ${side === "L" ? "bg-[#10B1B6] text-white border-[#10B1B6]" : "bg-white text-slate-700 border-slate-200 hover:border-[#10B1B6]/50"}`}>
                        <ArrowDownLeft className="w-4 h-4" /> Left Wing
                    </button>
                    <button type="button" onClick={() => handleSide("R")}
                        className={`h-11 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors
                            ${side === "R" ? "bg-[#E45B80] text-white border-[#E45B80]" : "bg-white text-slate-700 border-slate-200 hover:border-[#E45B80]/50"}`}>
                        <ArrowDownRight className="w-4 h-4" /> Right Wing
                    </button>
                </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            {!result && (
                <button type="button" onClick={verify} disabled={loading}
                    className="w-full h-11 rounded-xl bg-[#2172E5] hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                    Verify &amp; Continue
                </button>
            )}

            {result && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 divide-y divide-blue-100 overflow-hidden">
                    <div className="px-4 py-3 flex items-center justify-between">
                        <div>
                            <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Sponsor&apos;s ID</div>
                            <div className="font-mono font-bold text-[#2172E5]">{result.sponsor_code}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Sponsor&apos;s Name</div>
                            <div className="font-semibold text-slate-800">{result.sponsor_name}</div>
                        </div>
                    </div>

                    {result.same_as_sponsor ? (
                        <div className="px-4 py-3 flex items-center gap-2 bg-green-50">
                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                            <p className="text-sm text-green-700">
                                <span className="font-semibold">{side === "L" ? "Left" : "Right"} slot is available</span> — member will be placed directly under this sponsor.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Adjusted To ID</div>
                                    <div className="font-mono font-bold text-slate-700">{result.adjusted_to_code}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Adjusted To Name</div>
                                    <div className="font-semibold text-slate-800">{result.adjusted_to_name}</div>
                                </div>
                            </div>
                            <div className="px-4 py-2 bg-amber-50">
                                <p className="text-[11px] text-amber-700">
                                    Spillover applied — {side === "L" ? "Left" : "Right"} slot under sponsor is taken.
                                    Member will be placed under <span className="font-semibold">{result.adjusted_to_name}</span>.
                                </p>
                            </div>
                        </>
                    )}

                    <div className="px-4 py-3">
                        <button type="button" onClick={() => onConfirm({ referral_code: result.sponsor_code, side })}
                            className="w-full h-10 rounded-xl bg-[#2172E5] hover:bg-blue-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                            Confirm &amp; Continue <ChevronRight className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => setResult(null)}
                            className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600 text-center">
                            Change sponsor or side
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Step 2: Plan selection ────────────────────────────────────────────────────
function Step2({ onSelect, onBack }) {
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/products").then(r => setProducts(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-4">
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2172E5]" />
                </div>
            )}

            {!loading && products.map(p => (
                <button key={p.id} type="button" onClick={() => setSelected(p)}
                    className={`w-full text-left rounded-xl border-2 p-4 transition-colors
                        ${selected?.id === p.id ? "border-[#2172E5] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-semibold text-slate-900">{p.name}</div>
                            {p.description && <div className="mt-0.5 text-sm text-slate-500">{p.description}</div>}
                        </div>
                        <div className="shrink-0 text-right">
                            <div className="text-lg font-black text-[#2172E5]">&#8377;{Number(p.price).toLocaleString("en-IN")}</div>
                            {selected?.id === p.id && <CheckCircle2 className="w-5 h-5 text-[#2172E5] mt-1 ml-auto" />}
                        </div>
                    </div>
                </button>
            ))}

            {!loading && products.length === 0 && (
                <p className="text-center text-slate-500 py-8">No plans available at the moment.</p>
            )}

            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                    className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Back
                </button>
                <button type="button" disabled={!selected} onClick={() => onSelect(selected)}
                    className="flex-[2] h-11 rounded-xl bg-[#2172E5] hover:bg-blue-700 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40 transition-colors">
                    Continue <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ── Step 3: Personal info ─────────────────────────────────────────────────────
function Step3({ sponsorData, product, onBack, onSuccess }) {
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", password: "",
        occupation: "", guardian_name: "", address: "",
    });
    const [loading, setLoading] = useState(false);
    const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                full_name:     form.full_name,
                email:         form.email         || undefined,
                phone:         form.phone         || undefined,
                password:      form.password,
                address:       form.address       || undefined,
                occupation:    form.occupation    || undefined,
                guardian_name: form.guardian_name || undefined,
                referral_code: sponsorData.referral_code,
                side:          sponsorData.side,
                product_id:    product.id,
            };
            await api.post("/auth/register", payload);
            toast.success("Member registered successfully!");
            onSuccess({ ...form, product });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {/* Summary strip */}
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex gap-6 flex-wrap">
                <div>
                    <span className="text-slate-400 text-xs">Sponsor</span>
                    <div className="font-semibold text-slate-700">
                        {sponsorData.referral_code} &middot; {sponsorData.side === "L" ? "Left Wing" : "Right Wing"}
                    </div>
                </div>
                <div className="border-l border-slate-200 pl-6">
                    <span className="text-slate-400 text-xs">Plan</span>
                    <div className="font-semibold text-slate-700">
                        {product.name} &mdash; &#8377;{Number(product.price).toLocaleString("en-IN")}
                    </div>
                </div>
            </div>

            <label className="block">
                <span className={LABEL}>Full Name <span className="text-red-500">*</span></span>
                <input required value={form.full_name} onChange={set("full_name")} className={FIELD} placeholder="As per Aadhaar / PAN" />
            </label>
            <label className="block">
                <span className={LABEL}>Mobile Number</span>
                <input type="tel" maxLength={10} value={form.phone} onChange={set("phone")} className={FIELD} placeholder="10-digit mobile number" />
            </label>
            <label className="block">
                <span className={LABEL}>Email ID <span className="text-slate-400 font-normal text-xs">(optional)</span></span>
                <input type="email" value={form.email} onChange={set("email")} className={FIELD} placeholder="member@example.com" />
            </label>
            <label className="block">
                <span className={LABEL}>Occupation</span>
                <input value={form.occupation} onChange={set("occupation")} className={FIELD} placeholder="e.g. Business, Farmer, Student" />
            </label>
            <label className="block">
                <span className={LABEL}>Father / Spouse Name</span>
                <input value={form.guardian_name} onChange={set("guardian_name")} className={FIELD} placeholder="Father's or Spouse's full name" />
            </label>
            <label className="block">
                <span className={LABEL}>Residential Address</span>
                <textarea rows={3} value={form.address} onChange={set("address")} className={TEXTAREA} placeholder="Full address including pincode" />
            </label>
            <label className="block">
                <span className={LABEL}>Temporary Password <span className="text-red-500">*</span></span>
                <input
                    required
                    type="text"
                    minLength={6}
                    value={form.password}
                    onChange={set("password")}
                    className={FIELD}
                    placeholder="Set a temporary password — share with member"
                />
                <span className="text-[11px] text-slate-400 mt-1 block">The member should change this after their first login.</span>
            </label>

            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                    className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Back
                </button>
                <button type="submit" disabled={loading}
                    className="flex-[2] h-11 rounded-xl bg-[#2172E5] hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Register Member
                </button>
            </div>
        </form>
    );
}

// ── Success card ──────────────────────────────────────────────────────────────
function SuccessCard({ result, onAddAnother }) {
    const [copied, setCopied] = useState(false);

    const copyPassword = () => {
        navigator.clipboard.writeText(result.password).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
                <div className="flex items-center gap-2 text-emerald-700 font-bold mb-3">
                    <CheckCircle2 className="w-5 h-5" /> Member registered!
                </div>
                <div className="space-y-1 text-sm text-slate-700">
                    <div><span className="text-slate-500">Name:</span> {result.full_name}</div>
                    {result.email && <div><span className="text-slate-500">Email:</span> {result.email}</div>}
                    {result.phone && <div><span className="text-slate-500">Phone:</span> {result.phone}</div>}
                    <div>
                        <span className="text-slate-500">Plan:</span>{" "}
                        {result.product.name} &mdash; &#8377;{Number(result.product.price).toLocaleString("en-IN")}
                    </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-xs text-slate-500 mb-0.5">Temporary password (share with member)</div>
                        <div className="font-mono font-bold text-slate-800">{result.password}</div>
                    </div>
                    <button onClick={copyPassword} className="flex items-center gap-1 text-xs text-[#2172E5] font-semibold hover:underline shrink-0">
                        {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? "Copied!" : "Copy"}
                    </button>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                    Ask the new member to log in and complete KYC verification.
                </p>
            </div>

            <button
                type="button"
                onClick={onAddAnother}
                className="w-full h-11 rounded-xl border-2 border-[#2172E5] text-[#2172E5] font-semibold text-sm hover:bg-blue-50 transition-colors"
            >
                + Register Another Member
            </button>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AddMember() {
    const { user } = useAuth();
    const [step, setStep] = useState(0);
    const [sponsorData, setSponsorData] = useState(null);
    const [product, setProduct] = useState(null);
    const [result, setResult] = useState(null);

    const leftCount  = user?.left_active_count  ?? 0;
    const rightCount = user?.right_active_count ?? 0;
    const symmetryPct = Math.max(leftCount, rightCount) > 0
        ? Math.round((Math.min(leftCount, rightCount) / Math.max(leftCount, rightCount)) * 100)
        : 0;

    const reset = () => {
        setStep(0);
        setSponsorData(null);
        setProduct(null);
        setResult(null);
    };

    return (
        <AppShell>
            <div className="flex gap-6 items-start">

                {/* ── Main column ── */}
                <div className="flex-1 min-w-0 pb-12">

                    <div className="flex justify-between items-center mb-5">
                        <div>
                            <div className="overline">Member Management</div>
                            <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                                Add New Member
                            </h1>
                        </div>
                        {user?.referral_code && (
                            <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800">
                                Your ID: {user.referral_code}
                            </span>
                        )}
                    </div>

                    <div className="bg-indigo-50/60 border border-indigo-100/60 rounded-2xl p-4 mb-6">
                        <p className="text-xs text-slate-600 leading-relaxed">
                            Register a new partner under any Sponsor ID. Your ID{" "}
                            <b className="text-[#2172E5]">{user?.referral_code}</b>{" "}
                            is pre-filled — change it to place under a different member.
                        </p>
                    </div>

                    <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
                        {result ? (
                            <SuccessCard result={result} onAddAnother={reset} />
                        ) : (
                            <>
                                <StepIndicator current={step} />

                                {step === 0 && (
                                    <Step1
                                        defaultCode={user?.referral_code || ""}
                                        onConfirm={data => { setSponsorData(data); setStep(1); }}
                                    />
                                )}
                                {step === 1 && (
                                    <Step2
                                        onBack={() => setStep(0)}
                                        onSelect={p => { setProduct(p); setStep(2); }}
                                    />
                                )}
                                {step === 2 && (
                                    <Step3
                                        sponsorData={sponsorData}
                                        product={product}
                                        onBack={() => setStep(1)}
                                        onSuccess={data => setResult(data)}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Right sidebar ── */}
                <div className="w-72 hidden xl:flex flex-col flex-shrink-0 bg-white rounded-3xl shadow-sm p-6 self-stretch justify-between" style={{ minHeight: "520px" }}>
                    <div className="space-y-6">
                        <div>
                            <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Wing Ratio Status</span>
                            <h3 className="text-lg font-bold text-slate-900 mt-1">Network Symmetry</h3>
                        </div>

                        <div className="p-5 bg-[#EDF2F9] rounded-2xl border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-500">Wing symmetry</span>
                                <span className="text-xs font-bold text-[#2172E5]">{symmetryPct}% matched</span>
                            </div>
                            <div className="w-full bg-slate-200 h-3.5 rounded-full overflow-hidden">
                                <div
                                    className="bg-[#2172E5] h-full rounded-full transition-all duration-500"
                                    style={{ width: `${symmetryPct}%` }}
                                />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2">
                                Matching {Math.min(leftCount, rightCount)} pairs of{" "}
                                {Math.max(leftCount, rightCount)} potential channels.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-slate-400 tracking-widest uppercase">Team Channels</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center p-3 bg-teal-50 rounded-xl border border-teal-100/50">
                                    <span className="text-xs font-bold text-teal-800">Left Wing Leaders</span>
                                    <div className="flex -space-x-1.5">
                                        <div className="w-6 h-6 rounded-full bg-[#10B1B6]/20 text-teal-600 flex items-center justify-center font-bold text-[8px] border border-white">LW</div>
                                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[8px] border border-white">SP</div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-xl border border-pink-100/50">
                                    <span className="text-xs font-bold text-pink-800">Right Wing Growth</span>
                                    <div className="flex -space-x-1.5">
                                        <div className="w-6 h-6 rounded-full bg-[#E45B80]/20 text-pink-600 flex items-center justify-center font-bold text-[8px] border border-white">RG</div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl border border-purple-100/50">
                                    <span className="text-xs font-bold text-purple-800">Direct Referrals</span>
                                    <div className="flex -space-x-1.5">
                                        <div className="w-6 h-6 rounded-full bg-[#7059FD]/20 text-purple-600 flex items-center justify-center font-bold text-[8px] border border-white">DR</div>
                                        <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-[8px] border border-white">AM</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                        <p className="text-xs text-slate-400">
                            Left: <span className="font-bold text-teal-600">{leftCount}</span> active &nbsp;|&nbsp;
                            Right: <span className="font-bold text-pink-600">{rightCount}</span> active
                        </p>
                    </div>
                </div>

            </div>
        </AppShell>
    );
}
